import { useState, useEffect } from "react";
import axios from "axios";
import { API, getAuthHeader, formatDateDDMMYYYY, getTodayDDMMYYYY } from "@/App";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { 
  FolderKanban, Plus, MoreVertical, Calendar, Users, CheckCircle2, 
  Clock, AlertCircle, Trash2, Edit, ChevronRight, ChevronDown, Bell,
  ListTodo, User, ArrowLeft, X
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const ProjectsPanel = ({ user }) => {
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [viewMode, setViewMode] = useState("grid"); // grid, kanban, detail
  const [detailProject, setDetailProject] = useState(null);
  const [projectTasks, setProjectTasks] = useState([]);
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [expandedProjects, setExpandedProjects] = useState({});
  
  const [newProject, setNewProject] = useState({
    name: "",
    description: "",
    status: "planning",
    priority: "medium",
    due_date: ""
  });

  const [newTask, setNewTask] = useState({
    title: "",
    description: "",
    assigned_to: "",
    priority: "medium",
    due_date: "",
    reminder_enabled: false,
    reminder_date: "",
    reminder_time: "09:00"
  });

  const statuses = [
    { value: "planning", label: "Planning", color: "bg-slate-100 text-slate-700", icon: Clock },
    { value: "in_progress", label: "In Progress", color: "bg-blue-100 text-blue-700", icon: AlertCircle },
    { value: "review", label: "Review", color: "bg-amber-100 text-amber-700", icon: Clock },
    { value: "completed", label: "Completed", color: "bg-green-100 text-green-700", icon: CheckCircle2 }
  ];

  const priorities = [
    { value: "low", label: "Low", color: "bg-slate-100 text-slate-600" },
    { value: "medium", label: "Medium", color: "bg-orange-100 text-orange-600" },
    { value: "high", label: "High", color: "bg-red-100 text-red-600" }
  ];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [projectsRes, usersRes] = await Promise.all([
        axios.get(`${API}/projects`, { headers: getAuthHeader() }),
        axios.get(`${API}/users`, { headers: getAuthHeader() })
      ]);
      setProjects(projectsRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      toast.error("Failed to fetch projects");
    } finally {
      setLoading(false);
    }
  };

  const fetchProjectTasks = async (projectId) => {
    try {
      const response = await axios.get(`${API}/project-tasks?project_id=${projectId}`, { headers: getAuthHeader() });
      setProjectTasks(response.data);
    } catch (error) {
      toast.error("Failed to fetch tasks");
    }
  };

  const handleCreateProject = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/projects`, newProject, { headers: getAuthHeader() });
      toast.success("Project created successfully");
      setCreateDialogOpen(false);
      setNewProject({ name: "", description: "", status: "planning", priority: "medium", due_date: "" });
      fetchData();
    } catch (error) {
      toast.error("Failed to create project");
    }
  };

  const handleUpdateProject = async (e) => {
    e.preventDefault();
    if (!selectedProject) return;
    try {
      await axios.put(`${API}/projects/${selectedProject.id}`, {
        name: selectedProject.name,
        description: selectedProject.description,
        status: selectedProject.status,
        priority: selectedProject.priority,
        due_date: selectedProject.due_date
      }, { headers: getAuthHeader() });
      toast.success("Project updated");
      setEditDialogOpen(false);
      setSelectedProject(null);
      fetchData();
    } catch (error) {
      toast.error("Failed to update project");
    }
  };

  const handleDeleteProject = async (projectId) => {
    if (!window.confirm("Delete this project and all its tasks?")) return;
    try {
      await axios.delete(`${API}/projects/${projectId}`, { headers: getAuthHeader() });
      toast.success("Project deleted");
      if (detailProject?.id === projectId) {
        setDetailProject(null);
        setViewMode("grid");
      }
      fetchData();
    } catch (error) {
      toast.error("Failed to delete project");
    }
  };

  const handleStatusChange = async (projectId, newStatus) => {
    try {
      await axios.put(`${API}/projects/${projectId}`, { status: newStatus }, { headers: getAuthHeader() });
      toast.success("Status updated");
      fetchData();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  // Task functions
  const handleCreateTask = async (e) => {
    e.preventDefault();
    if (!detailProject) return;
    try {
      await axios.post(`${API}/project-tasks`, {
        ...newTask,
        project_id: detailProject.id
      }, { headers: getAuthHeader() });
      toast.success("Task created");
      setTaskDialogOpen(false);
      resetTaskForm();
      fetchProjectTasks(detailProject.id);
      fetchData(); // Refresh project progress
    } catch (error) {
      toast.error("Failed to create task");
    }
  };

  const handleUpdateTask = async (e) => {
    e.preventDefault();
    if (!editingTask) return;
    try {
      await axios.put(`${API}/project-tasks/${editingTask.id}`, newTask, { headers: getAuthHeader() });
      toast.success("Task updated");
      setTaskDialogOpen(false);
      setEditingTask(null);
      resetTaskForm();
      fetchProjectTasks(detailProject.id);
      fetchData();
    } catch (error) {
      toast.error("Failed to update task");
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm("Delete this task?")) return;
    try {
      await axios.delete(`${API}/project-tasks/${taskId}`, { headers: getAuthHeader() });
      toast.success("Task deleted");
      fetchProjectTasks(detailProject.id);
      fetchData();
    } catch (error) {
      toast.error("Failed to delete task");
    }
  };

  const handleTaskStatusChange = async (taskId, newStatus) => {
    try {
      await axios.put(`${API}/project-tasks/${taskId}`, { status: newStatus }, { headers: getAuthHeader() });
      toast.success("Task updated");
      fetchProjectTasks(detailProject.id);
      fetchData();
    } catch (error) {
      toast.error("Failed to update task");
    }
  };

  const resetTaskForm = () => {
    setNewTask({
      title: "",
      description: "",
      assigned_to: "",
      priority: "medium",
      due_date: "",
      reminder_enabled: false,
      reminder_date: "",
      reminder_time: "09:00"
    });
  };

  const openTaskDialog = (task = null) => {
    if (task) {
      setEditingTask(task);
      setNewTask({
        title: task.title,
        description: task.description || "",
        assigned_to: task.assigned_to || "",
        priority: task.priority,
        due_date: task.due_date || "",
        reminder_enabled: task.reminder_enabled || false,
        reminder_date: task.reminder_date || "",
        reminder_time: task.reminder_time || "09:00"
      });
    } else {
      setEditingTask(null);
      resetTaskForm();
    }
    setTaskDialogOpen(true);
  };

  const openProjectDetail = async (project) => {
    setDetailProject(project);
    setViewMode("detail");
    await fetchProjectTasks(project.id);
  };

  const getStatusInfo = (status) => statuses.find(s => s.value === status) || statuses[0];
  const getPriorityInfo = (priority) => priorities.find(p => p.value === priority) || priorities[1];

  const formatDate = (dateString) => {
    if (!dateString) return null;
    return formatDateDDMMYYYY(dateString);
  };

  // Toggle inline tasks
  const toggleProjectExpand = async (projectId) => {
    if (expandedProjects[projectId]) {
      setExpandedProjects(prev => ({ ...prev, [projectId]: false }));
    } else {
      setExpandedProjects(prev => ({ ...prev, [projectId]: true }));
      // Fetch tasks for this project
      try {
        const response = await axios.get(`${API}/project-tasks?project_id=${projectId}`, { headers: getAuthHeader() });
        setExpandedProjects(prev => ({ ...prev, [projectId]: response.data }));
      } catch (error) {
        console.error("Failed to fetch tasks");
      }
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading projects...</div>;
  }

  // Project Detail View
  if (viewMode === "detail" && detailProject) {
    const statusInfo = getStatusInfo(detailProject.status);
    const priorityInfo = getPriorityInfo(detailProject.priority);
    const pendingTasks = projectTasks.filter(t => t.status === 'pending');
    const inProgressTasks = projectTasks.filter(t => t.status === 'in_progress');
    const completedTasks = projectTasks.filter(t => t.status === 'completed');

    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <Button variant="ghost" onClick={() => { setViewMode("grid"); setDetailProject(null); }}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Projects
          </Button>
          <Button onClick={() => openTaskDialog()} className="bg-blue-600 hover:bg-blue-700">
            <Plus className="w-4 h-4 mr-2" />
            Add Task
          </Button>
        </div>

        {/* Project Info Card */}
        <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-lg">
          <CardHeader>
            <div className="flex items-start justify-between">
              <div>
                <CardTitle className="text-2xl">{detailProject.name}</CardTitle>
                <CardDescription className="mt-1">{detailProject.description}</CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <Badge className={statusInfo.color}>{statusInfo.label}</Badge>
                <Badge className={priorityInfo.color}>{priorityInfo.label}</Badge>
              </div>
            </div>
            <div className="flex items-center gap-6 mt-4 text-sm text-slate-600">
              {detailProject.due_date && (
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  Due: {formatDate(detailProject.due_date)}
                </span>
              )}
              <span className="flex items-center gap-1">
                <ListTodo className="w-4 h-4" />
                {detailProject.completed_tasks || 0}/{detailProject.total_tasks || 0} tasks completed
              </span>
            </div>
            <div className="mt-4">
              <div className="flex items-center justify-between text-sm mb-1">
                <span className="text-slate-600">Progress</span>
                <span className="font-medium">{detailProject.progress || 0}%</span>
              </div>
              <Progress value={detailProject.progress || 0} className="h-2" />
            </div>
          </CardHeader>
        </Card>

        {/* Tasks Kanban View */}
        <div className="grid grid-cols-3 gap-4">
          {/* Pending */}
          <Card className="bg-slate-50 border-slate-200">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-amber-500" />
                  Pending ({pendingTasks.length})
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-2">
              {pendingTasks.map(task => (
                <TaskCard 
                  key={task.id} 
                  task={task} 
                  onStatusChange={handleTaskStatusChange}
                  onEdit={() => openTaskDialog(task)}
                  onDelete={() => handleDeleteTask(task.id)}
                  formatDate={formatDate}
                  getPriorityInfo={getPriorityInfo}
                />
              ))}
              {pendingTasks.length === 0 && (
                <p className="text-sm text-slate-400 text-center py-4">No pending tasks</p>
              )}
            </CardContent>
          </Card>

          {/* In Progress */}
          <Card className="bg-blue-50 border-blue-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Clock className="w-4 h-4 text-blue-500" />
                In Progress ({inProgressTasks.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {inProgressTasks.map(task => (
                <TaskCard 
                  key={task.id} 
                  task={task} 
                  onStatusChange={handleTaskStatusChange}
                  onEdit={() => openTaskDialog(task)}
                  onDelete={() => handleDeleteTask(task.id)}
                  formatDate={formatDate}
                  getPriorityInfo={getPriorityInfo}
                />
              ))}
              {inProgressTasks.length === 0 && (
                <p className="text-sm text-slate-400 text-center py-4">No tasks in progress</p>
              )}
            </CardContent>
          </Card>

          {/* Completed */}
          <Card className="bg-green-50 border-green-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-500" />
                Completed ({completedTasks.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {completedTasks.map(task => (
                <TaskCard 
                  key={task.id} 
                  task={task} 
                  onStatusChange={handleTaskStatusChange}
                  onEdit={() => openTaskDialog(task)}
                  onDelete={() => handleDeleteTask(task.id)}
                  formatDate={formatDate}
                  getPriorityInfo={getPriorityInfo}
                  isCompleted
                />
              ))}
              {completedTasks.length === 0 && (
                <p className="text-sm text-slate-400 text-center py-4">No completed tasks</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Task Dialog */}
        <Dialog open={taskDialogOpen} onOpenChange={setTaskDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{editingTask ? "Edit Task" : "Create Task"}</DialogTitle>
              <DialogDescription>
                {editingTask ? "Update task details" : "Add a new task to this project"}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={editingTask ? handleUpdateTask : handleCreateTask} className="space-y-4">
              <div className="space-y-2">
                <Label>Task Title *</Label>
                <Input
                  value={newTask.title}
                  onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                  placeholder="Enter task title"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={newTask.description}
                  onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                  placeholder="Task description"
                  rows={2}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Assign To</Label>
                  <Select 
                    value={newTask.assigned_to || "unassigned"} 
                    onValueChange={(val) => setNewTask({ ...newTask, assigned_to: val === "unassigned" ? "" : val })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select member" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unassigned">Unassigned</SelectItem>
                      {users.map(u => (
                        <SelectItem key={u.id} value={u.id}>{u.name} ({u.team})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Priority</Label>
                  <Select value={newTask.priority} onValueChange={(val) => setNewTask({ ...newTask, priority: val })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Due Date</Label>
                <Input
                  type="text"
                  placeholder="dd/mm/yyyy"
                  value={newTask.due_date}
                  onChange={(e) => setNewTask({ ...newTask, due_date: e.target.value })}
                />
              </div>

              {/* Reminder Section */}
              <div className="space-y-3 p-3 bg-slate-50 rounded-lg">
                <div className="flex items-center justify-between">
                  <Label className="flex items-center gap-2">
                    <Bell className="w-4 h-4 text-orange-500" />
                    Set Reminder
                  </Label>
                  <Switch
                    checked={newTask.reminder_enabled}
                    onCheckedChange={(checked) => setNewTask({ ...newTask, reminder_enabled: checked })}
                  />
                </div>
                
                {newTask.reminder_enabled && (
                  <div className="grid grid-cols-2 gap-3 mt-2">
                    <div className="space-y-1">
                      <Label className="text-xs">Reminder Date</Label>
                      <Input
                        type="text"
                        placeholder="dd/mm/yyyy"
                        value={newTask.reminder_date}
                        onChange={(e) => setNewTask({ ...newTask, reminder_date: e.target.value })}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Reminder Time</Label>
                      <Input
                        type="time"
                        value={newTask.reminder_time}
                        onChange={(e) => setNewTask({ ...newTask, reminder_time: e.target.value })}
                      />
                    </div>
                  </div>
                )}
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setTaskDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                  {editingTask ? "Update Task" : "Create Task"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Grid View
  return (
    <div className="space-y-6">
      <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-lg">
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <CardTitle className="flex items-center gap-2">
                <FolderKanban className="w-5 h-5 text-purple-600" />
                Projects
              </CardTitle>
              <CardDescription>Manage team projects with tasks and deadlines</CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex border rounded-lg overflow-hidden">
                <Button 
                  variant={viewMode === "grid" ? "default" : "ghost"} 
                  size="sm"
                  onClick={() => setViewMode("grid")}
                  className={viewMode === "grid" ? "bg-purple-600" : ""}
                >
                  Grid
                </Button>
                <Button 
                  variant={viewMode === "kanban" ? "default" : "ghost"} 
                  size="sm"
                  onClick={() => setViewMode("kanban")}
                  className={viewMode === "kanban" ? "bg-purple-600" : ""}
                >
                  Kanban
                </Button>
              </div>
              <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-purple-600 hover:bg-purple-700">
                    <Plus className="w-4 h-4 mr-2" />
                    New Project
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Create New Project</DialogTitle>
                    <DialogDescription>Start a new project for your team</DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleCreateProject} className="space-y-4">
                    <div className="space-y-2">
                      <Label>Project Name *</Label>
                      <Input
                        value={newProject.name}
                        onChange={(e) => setNewProject({ ...newProject, name: e.target.value })}
                        placeholder="Enter project name"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Description</Label>
                      <Textarea
                        value={newProject.description}
                        onChange={(e) => setNewProject({ ...newProject, description: e.target.value })}
                        placeholder="Project description"
                        rows={2}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Priority</Label>
                        <Select value={newProject.priority} onValueChange={(val) => setNewProject({ ...newProject, priority: val })}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="low">Low</SelectItem>
                            <SelectItem value="medium">Medium</SelectItem>
                            <SelectItem value="high">High</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Due Date</Label>
                        <Input
                          type="text"
                          placeholder="dd/mm/yyyy"
                          value={newProject.due_date}
                          onChange={(e) => setNewProject({ ...newProject, due_date: e.target.value })}
                        />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)}>
                        Cancel
                      </Button>
                      <Button type="submit" className="bg-purple-600 hover:bg-purple-700">
                        Create Project
                      </Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </CardHeader>

        <CardContent>
          {projects.length === 0 ? (
            <div className="text-center py-12">
              <FolderKanban className="w-16 h-16 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">No projects yet</p>
              <p className="text-sm text-slate-400">Create your first project to get started</p>
            </div>
          ) : viewMode === "kanban" ? (
            // Kanban View
            <div className="grid grid-cols-4 gap-4">
              {statuses.map(status => {
                const statusProjects = projects.filter(p => p.status === status.value);
                return (
                  <div key={status.value} className="space-y-2">
                    <div className={`p-2 rounded-lg ${status.color} font-medium text-sm flex items-center justify-between`}>
                      <span>{status.label}</span>
                      <Badge variant="secondary" className="text-xs">{statusProjects.length}</Badge>
                    </div>
                    <div className="space-y-2 min-h-[200px]">
                      {statusProjects.map(project => (
                        <ProjectCard 
                          key={project.id}
                          project={project}
                          onView={() => openProjectDetail(project)}
                          onEdit={() => { setSelectedProject(project); setEditDialogOpen(true); }}
                          onDelete={() => handleDeleteProject(project.id)}
                          onStatusChange={handleStatusChange}
                          formatDate={formatDate}
                          statuses={statuses}
                          compact
                        />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            // Grid View
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {projects.map(project => (
                <ProjectCard 
                  key={project.id}
                  project={project}
                  onView={() => openProjectDetail(project)}
                  onEdit={() => { setSelectedProject(project); setEditDialogOpen(true); }}
                  onDelete={() => handleDeleteProject(project.id)}
                  onStatusChange={handleStatusChange}
                  formatDate={formatDate}
                  statuses={statuses}
                  priorities={priorities}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Project Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Project</DialogTitle>
            <DialogDescription>Update project details</DialogDescription>
          </DialogHeader>
          {selectedProject && (
            <form onSubmit={handleUpdateProject} className="space-y-4">
              <div className="space-y-2">
                <Label>Project Name *</Label>
                <Input
                  value={selectedProject.name}
                  onChange={(e) => setSelectedProject({ ...selectedProject, name: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={selectedProject.description || ""}
                  onChange={(e) => setSelectedProject({ ...selectedProject, description: e.target.value })}
                  rows={2}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select value={selectedProject.status} onValueChange={(val) => setSelectedProject({ ...selectedProject, status: val })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {statuses.map(s => (
                        <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Priority</Label>
                  <Select value={selectedProject.priority} onValueChange={(val) => setSelectedProject({ ...selectedProject, priority: val })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Due Date</Label>
                <Input
                  type="text"
                  placeholder="dd/mm/yyyy"
                  value={selectedProject.due_date || ""}
                  onChange={(e) => setSelectedProject({ ...selectedProject, due_date: e.target.value })}
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-purple-600 hover:bg-purple-700">
                  Save Changes
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Task Card Component
const TaskCard = ({ task, onStatusChange, onEdit, onDelete, formatDate, getPriorityInfo, isCompleted = false }) => {
  const priorityInfo = getPriorityInfo(task.priority);
  
  return (
    <div className={`p-3 bg-white rounded-lg border shadow-sm ${isCompleted ? 'opacity-70' : ''}`}>
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-2">
          <Checkbox
            checked={task.status === 'completed'}
            onCheckedChange={(checked) => onStatusChange(task.id, checked ? 'completed' : 'pending')}
          />
          <div>
            <p className={`text-sm font-medium ${isCompleted ? 'line-through text-slate-500' : ''}`}>
              {task.title}
            </p>
            {task.description && (
              <p className={`text-xs text-slate-500 mt-0.5 ${isCompleted ? 'line-through' : ''}`}>
                {task.description}
              </p>
            )}
            <div className="flex items-center gap-2 mt-1.5 flex-wrap">
              <Badge className={`${priorityInfo.color} text-xs px-1.5 py-0`}>
                {task.priority}
              </Badge>
              {task.assigned_to_name && (
                <span className="text-xs text-slate-500 flex items-center gap-1">
                  <User className="w-3 h-3" />
                  {task.assigned_to_name}
                </span>
              )}
              {task.due_date && (
                <span className="text-xs text-slate-500 flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  {formatDate(task.due_date)}
                </span>
              )}
              {task.reminder_enabled && (
                <Bell className="w-3 h-3 text-orange-500" />
              )}
            </div>
          </div>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
              <MoreVertical className="w-3 h-3" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => onStatusChange(task.id, 'pending')}>
              <AlertCircle className="w-4 h-4 mr-2" /> Set Pending
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onStatusChange(task.id, 'in_progress')}>
              <Clock className="w-4 h-4 mr-2" /> Set In Progress
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onStatusChange(task.id, 'completed')}>
              <CheckCircle2 className="w-4 h-4 mr-2" /> Set Completed
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onEdit}>
              <Edit className="w-4 h-4 mr-2" /> Edit
            </DropdownMenuItem>
            <DropdownMenuItem className="text-red-600" onClick={onDelete}>
              <Trash2 className="w-4 h-4 mr-2" /> Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};

// Project Card Component
const ProjectCard = ({ project, onView, onEdit, onDelete, onStatusChange, formatDate, statuses, priorities, compact = false }) => {
  const statusInfo = statuses.find(s => s.value === project.status) || statuses[0];
  const priorityInfo = priorities?.find(p => p.value === project.priority);
  const StatusIcon = statusInfo.icon;
  
  return (
    <Card className={`bg-white border hover:shadow-md transition-shadow cursor-pointer ${compact ? 'p-2' : ''}`} onClick={onView}>
      <CardContent className={compact ? "p-2" : "p-4"}>
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <h3 className={`font-semibold truncate ${compact ? 'text-sm' : ''}`}>{project.name}</h3>
            {!compact && project.description && (
              <p className="text-sm text-slate-500 line-clamp-2 mt-1">{project.description}</p>
            )}
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                <MoreVertical className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onView(); }}>
                <ChevronRight className="w-4 h-4 mr-2" /> View Tasks
              </DropdownMenuItem>
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onEdit(); }}>
                <Edit className="w-4 h-4 mr-2" /> Edit
              </DropdownMenuItem>
              <DropdownMenuItem className="text-red-600" onClick={(e) => { e.stopPropagation(); onDelete(); }}>
                <Trash2 className="w-4 h-4 mr-2" /> Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className={`flex flex-wrap items-center gap-2 ${compact ? 'mt-2' : 'mt-3'}`}>
          <Badge className={`${statusInfo.color} ${compact ? 'text-xs' : ''}`}>
            <StatusIcon className="w-3 h-3 mr-1" />
            {statusInfo.label}
          </Badge>
          {priorityInfo && (
            <Badge className={`${priorityInfo.color} ${compact ? 'text-xs' : ''}`}>
              {project.priority}
            </Badge>
          )}
        </div>

        {/* Progress Bar */}
        <div className={`${compact ? 'mt-2' : 'mt-3'}`}>
          <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
            <span>{project.completed_tasks || 0}/{project.total_tasks || 0} tasks</span>
            <span>{project.progress || 0}%</span>
          </div>
          <Progress value={project.progress || 0} className="h-1.5" />
        </div>

        {!compact && project.due_date && (
          <div className="mt-3 flex items-center gap-1 text-xs text-slate-500">
            <Calendar className="w-3 h-3" />
            Due: {formatDate(project.due_date)}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ProjectsPanel;
